import { launchDarklyMockFlagData } from '@mocks/data/launch-darkly/launchDarklyMockData';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import {
  ANONYMOUS_CONTEXT,
  type LaunchDarklyProviderProps,
} from './LaunchDarklyProvider.types';

/** Passthrough provider used when LD initialization fails */
const FallbackProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => children as React.ReactElement;

/**
 * Initializes LaunchDarkly SDK with an anonymous context.
 * User identification is handled separately by `useLDIdentify` hook,
 * which lives inside the AuthProvider tree where user data is available.
 */
const LaunchDarklyProvider = React.memo(
  ({
    children,
  }: Readonly<LaunchDarklyProviderProps>): React.ReactElement | null => {
    const [LDProvider, setLDProvider] = React.useState<React.ComponentType<{
      children: React.ReactNode;
    }> | null>(null);

    React.useEffect(() => {
      const initializeLaunchDarkly = async (): Promise<void> => {
        try {
          const Provider = await asyncWithLDProvider({
            clientSideID: import.meta.env.VITE_LAUNCHDARKLY_SDK_KEY,
            context: ANONYMOUS_CONTEXT,
            ...(import.meta.env.MODE === 'mock' && {
              flags: launchDarklyMockFlagData,
            }),
          });

          setLDProvider(() => Provider);
        } catch {
          setLDProvider(() => FallbackProvider);
        }
      };

      initializeLaunchDarkly();
    }, []);

    if (!LDProvider) {
      return <AppLoader />;
    }

    return <LDProvider>{children}</LDProvider>;
  }
);

LaunchDarklyProvider.displayName = 'LaunchDarklyProvider';

export { LaunchDarklyProvider };
